import { router } from '@/app/providers';
import { API } from '@/shared/api/modules';
import { queryClient } from '@/shared/api/query-client.instance';
import { globalSocket } from '@/shared/api/sockets';
import { resetAllStores } from '@/shared/lib/zustand';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  transformCurrentUserDtoToCurrentUser,
  transformCurrentUserDtoToSession,
} from '../api/lib';
import type { CurrentUser, Session } from './types';

type SessionState = Session & {
  user: CurrentUser;
};

type SessionActions = {
  deleteSession: () => void;
  checkSession: () => Promise<undefined>;
  refetchUser: () => Promise<void>;
  updateUser: (data: Partial<CurrentUser>) => void;
  logout: (config?: { withRequest: boolean }) => Promise<void> | void;
};

const initialState: SessionState = {
  accessToken: null,
  permissions: [],
  isAuthenticated: null,
  isSuperAdmin: null,
  isContractor: null,
  user: null,
};

export const useSessionStore = create<SessionState & SessionActions>()(
  immer((set, get) => ({
    ...initialState,
    deleteSession: () => {
      set((state) => {
        state.accessToken = null;
        state.isAuthenticated = false;
        state.isSuperAdmin = null;
        state.isContractor = null;
        state.permissions = [];
        state.user = null;
      });
    },
    checkSession: async () => {
      try {
        const { isValid, accessToken } = await API.auth.checkSession();

        if (!isValid || !accessToken) {
          set((state) => {
            state.accessToken = null;
            state.isAuthenticated = false;
          });
          return;
        }

        const currentUserDto = await API.users.getCurrentUser({
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        set(() => ({
          ...transformCurrentUserDtoToSession(currentUserDto),
          user: transformCurrentUserDtoToCurrentUser(currentUserDto),
          accessToken: accessToken,
        }));
      } catch (error) {
        get().deleteSession();
        console.error('Failed to check session:', error);
      }
    },
    refetchUser: async () => {
      try {
        const { accessToken } = get();

        const currentUserDto = await API.users.getCurrentUser({
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        set(() => ({
          user: transformCurrentUserDtoToCurrentUser(currentUserDto),
        }));
      } catch (error) {
        console.error('Failed to refetch user:', error);
      }
    },
    logout: async (data = { withRequest: true }) => {
      try {
        if (data.withRequest) {
          await API.auth.logout();
        }
      } catch (err) {
        console.error('Logout request failed', err);
      } finally {
        get().deleteSession();
        resetAllStores();
        globalSocket.disconnect();
        queryClient.clear();
        router.navigate({ to: '/' });
      }
    },
    updateUser: (user) => {
      set((state) => {
        state.user = { ...state.user, ...user } as CurrentUser;
      });
    },
  }))
);
